import type { DateString } from '@web-platform/shared-universal-types/src/dateString';
import type {
  SearchLeg,
  SearchLegPlace,
} from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/search';
import type { EntityId, Nullable } from '@web-platform/shared-universal-types-utils/src/utils';

export type NormalizedSearchLeg = {
  origin: SearchLegPlace;
  destination: SearchLegPlace;
  date: DateString;
  placeOfStay?: Nullable<EntityId>;
  is_anytime?: boolean;
};

const normalizeLeg = ({ return_date, ...leg }: SearchLeg): NormalizedSearchLeg => leg;

// Normalizes platform-divergent leg shapes into a consistent N-leg array (one leg per direction).
//
// Most cases (Acorn, Banana one-way, Banana multi-city): legs are already one-per-direction — pass through.
// Banana return: stores a single leg with return_date (e.g. [{EDI→LHR, return_date: '...'}])
//   — split into two legs: [EDI→LHR, LHR→EDI] so downstream consumers are platform-agnostic.
// TODO: LUNA-3866: Remove workarounds below once userSearch has been converged
export const normalizeSearchLegs = (legs: SearchLeg[]): NormalizedSearchLeg[] => {
  if (!legs?.length) return [];

  // Banana return trip: single leg with return_date — split into two normalized legs
  const { return_date } = legs[0];
  if (legs.length === 1 && return_date != null) {
    const outboundLeg = legs[0];
    const outbound = normalizeLeg(outboundLeg);
    return [
      outbound,
      {
        ...outbound,
        origin: outboundLeg.destination,
        destination: outboundLeg.origin,
        date: return_date,
        placeOfStay: undefined,
      },
    ];
  }

  // Banana one-way or multi-city: legs are already one-per-direction
  return legs.map(normalizeLeg);
};
